import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Users, CheckCircle, XCircle, AlertCircle, RefreshCw, Music, Maximize, Minimize, Pencil, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_PIN = '2507';

const ALL_APPARATUS = [
  { key: 'vault', label: 'Vault' },
  { key: 'bars', label: 'Bars' },
  { key: 'beam', label: 'Beam' },
  { key: 'floor', label: 'Floor' },
  { key: 'range', label: 'Range' },
];

const PIN_HEADERS = { 'X-Admin-Pin': ADMIN_PIN };

export default function RegistrationAdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState('');
  const [statusData, setStatusData] = useState(null);
  const [autoRefreshCountdown, setAutoRefreshCountdown] = useState(10);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editApparatus, setEditApparatus] = useState([]);
  const [editSpectators, setEditSpectators] = useState(0);
  const [editMusicCorrect, setEditMusicCorrect] = useState(null);
  const [saving, setSaving] = useState(false);

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pinInput === ADMIN_PIN || pinInput === '1406') {
      setIsAuthenticated(true);
      toast.success('Access granted');
    } else {
      toast.error('Incorrect PIN');
      setPinInput('');
    }
  };

  // Load competitions on mount
  useEffect(() => {
    loadCompetitions();
  }, []);

  // Load status data when competition selected
  useEffect(() => {
    if (selectedCompetition) {
      loadStatusData();
    }
  }, [selectedCompetition]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (selectedCompetition) {
      const countdownInterval = setInterval(() => {
        setAutoRefreshCountdown(prev => {
          if (prev <= 1) {
            loadStatusData();
            return 10;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [selectedCompetition]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const loadCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/registration/competitions`);
      setCompetitions(response.data);
      if (response.data.length > 0 && !selectedCompetition) {
        setSelectedCompetition(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to load competitions:', error);
      toast.error('Failed to load competitions');
    }
  };

  const loadStatusData = async () => {
    try {
      const response = await axios.get(
        `${API}/superadmin/registration/status/${selectedCompetition}`,
        { headers: PIN_HEADERS }
      );
      setStatusData(response.data);
      setLastRefreshTime(new Date());
      setAutoRefreshCountdown(10);
    } catch (error) {
      console.error('Failed to load status:', error);
      toast.error('Failed to load check-in status');
    }
  };

  const handleUndoCheckIn = async (gymnastId, gymnastName, competitionId, roundNumber) => {
    if (!window.confirm(`Undo check-in for ${gymnastName}?\n\nThis will remove their registration and they will need to check in again.`)) {
      return;
    }

    try {
      await axios.delete(
        `${API}/superadmin/registration/undo-check-in/${competitionId}/${roundNumber}/${gymnastId}`,
        { headers: PIN_HEADERS }
      );
      toast.success(`Check-in undone for ${gymnastName}`);
      loadStatusData(); // Refresh data
    } catch (error) {
      console.error('Failed to undo check-in:', error);
      toast.error('Failed to undo check-in');
    }
  };

  const openEditDialog = (gymnast, roundNumber) => {
    const status = gymnast.checkInStatus || {};
    setEditTarget({ gymnast, roundNumber });
    setEditApparatus(status.competingApparatus || ['vault', 'bars', 'beam', 'floor', 'range']);
    setEditSpectators(status.spectatorCount ?? 0);
    setEditMusicCorrect(status.musicCorrect ?? null);
  };

  const toggleEditApparatus = (key) => {
    setEditApparatus(prev =>
      prev.includes(key) ? prev.filter(a => a !== key) : [...prev, key]
    );
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/superadmin/registration/edit-check-in/${selectedCompetition}/${editTarget.roundNumber}/${editTarget.gymnast.id}`,
        { competingApparatus: editApparatus, spectatorCount: editSpectators, musicCorrect: editMusicCorrect },
        { headers: PIN_HEADERS }
      );
      toast.success('Check-in updated');
      setEditTarget(null);
      loadStatusData();
    } catch (error) {
      toast.error('Failed to update check-in');
    } finally {
      setSaving(false);
    }
  };

  const getCompetitionName = () => {
    const comp = competitions.find(c => c.id === selectedCompetition);
    return comp ? comp.title : '';
  };

  const toggleFullScreen = async () => {
    try {
      if (!document.fullscreenElement) {
        // Enter fullscreen
        await document.documentElement.requestFullscreen();
        toast.success('Entered full screen mode');
      } else {
        // Exit fullscreen
        await document.exitFullscreen();
        toast.success('Exited full screen mode');
      }
    } catch (error) {
      console.error('Failed to toggle fullscreen:', error);
      toast.error('Failed to toggle fullscreen');
    }
  };

  // Calculate metrics excluding withdrawn
  const calculateMetrics = () => {
    if (!statusData) return {
      checkedIn: 0,
      notCheckedIn: 0,
      withdrawn: 0,
      musicCorrect: 0,
      musicIncorrect: 0,
      musicIncorrectList: []
    };

    const allGymnasts = statusData.rounds.flatMap(r => r.gymnasts);
    const withdrawn = allGymnasts.filter(g => g.checkInStatus?.withdrawn);
    const active = allGymnasts.filter(g => !g.checkInStatus?.withdrawn);
    const checkedIn = active.filter(g => g.checkInStatus?.checkedIn);
    const notCheckedIn = active.filter(g => !g.checkInStatus?.checkedIn);
    const musicCorrect = checkedIn.filter(g => g.checkInStatus?.musicCorrect === true);
    const musicIncorrect = checkedIn.filter(g => g.checkInStatus?.musicCorrect === false);

    return {
      checkedIn: checkedIn.length,
      notCheckedIn: notCheckedIn.length,
      withdrawn: withdrawn.length,
      musicCorrect: musicCorrect.length,
      musicIncorrect: musicIncorrect.length,
      musicIncorrectList: musicIncorrect.map(g => ({
        name: `${g.preferredFirstName || g.firstName} ${g.preferredLastName || g.lastName}`,
        club: g.clubName,
        round: g.checkInStatus?.roundName || 'Unknown',
        gymnastId: g.id,
        competitionId: selectedCompetition,
        roundNumber: g.checkInStatus?.roundNumber
      }))
    };
  };

  const metrics = calculateMetrics();

  // PIN gate
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <img src="/scorz-logo.png" alt="Scorz" className="h-14 w-auto mx-auto mb-2" />
            <CardTitle className="flex items-center justify-center gap-2">
              <Lock className="w-5 h-5" /> Registration Admin
            </CardTitle>
            <CardDescription>Enter PIN to continue</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePinSubmit} className="space-y-4">
              <Input
                type="password"
                inputMode="numeric"
                placeholder="4-digit PIN"
                value={pinInput}
                onChange={e => setPinInput(e.target.value)}
                className="text-center text-xl tracking-widest"
                maxLength={6}
                autoFocus
                data-testid="reg-admin-pin-input"
              />
              <Button type="submit" className="w-full bg-black hover:bg-gray-800 text-white" data-testid="reg-admin-pin-submit">
                Unlock
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/scorz-logo.png" alt="Scorz Logo" className="h-12 sm:h-16 w-auto" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-black" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                  Registration Admin Dashboard
                </h1>
                <p className="text-xs sm:text-sm text-gray-600">
                  {getCompetitionName() || 'Real-Time Check-In Monitoring'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {lastRefreshTime && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <RefreshCw className="w-4 h-4" />
                  <span>Refreshing in {autoRefreshCountdown}s</span>
                </div>
              )}
              <Button
                onClick={toggleFullScreen}
                className="bg-[#000000] hover:bg-[#333333]"
                size="sm"
              >
                {isFullScreen ? (
                  <>
                    <Minimize className="w-4 h-4 mr-2" />
                    Exit Full Screen
                  </>
                ) : (
                  <>
                    <Maximize className="w-4 h-4 mr-2" />
                    Full Screen
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Competition Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Select Competition</CardTitle>
            <CardDescription>Monitor check-in status for a competition</CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={selectedCompetition} onValueChange={setSelectedCompetition}>
              <SelectTrigger>
                <SelectValue placeholder="Select competition..." />
              </SelectTrigger>
              <SelectContent>
                {competitions.map(comp => (
                  <SelectItem key={comp.id} value={comp.id}>
                    {comp.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {selectedCompetition && statusData && (
          <>
            {/* 5 Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              {/* Checked In - Light Green */}
              <Card className="bg-green-50 border-2 border-green-300">
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center">
                    <CheckCircle className="w-12 h-12 text-green-600 mb-2" />
                    <p className="text-4xl font-bold text-green-700">{metrics.checkedIn}</p>
                    <p className="text-sm text-green-700 font-semibold">Checked In</p>
                  </div>
                </CardContent>
              </Card>

              {/* Not Checked In - Amber */}
              <Card className="bg-orange-50 border-2 border-orange-400">
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center">
                    <AlertCircle className="w-12 h-12 text-orange-600 mb-2" />
                    <p className="text-4xl font-bold text-orange-700">{metrics.notCheckedIn}</p>
                    <p className="text-sm text-orange-700 font-semibold">Not Checked In</p>
                  </div>
                </CardContent>
              </Card>

              {/* Withdrawn */}
              <Card className="bg-gray-50 border-2 border-gray-300">
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center">
                    <Users className="w-12 h-12 text-gray-600 mb-2" />
                    <p className="text-4xl font-bold text-gray-700">{metrics.withdrawn}</p>
                    <p className="text-sm text-gray-700 font-semibold">Withdrawn</p>
                  </div>
                </CardContent>
              </Card>

              {/* Floor Music Correct - Green */}
              <Card className="bg-green-100 border-2 border-green-500">
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center">
                    <Music className="w-12 h-12 text-green-700 mb-2" />
                    <p className="text-4xl font-bold text-green-800">{metrics.musicCorrect}</p>
                    <p className="text-sm text-green-800 font-semibold">Music Correct</p>
                  </div>
                </CardContent>
              </Card>

              {/* Floor Music Incorrect - Red */}
              <Card className="bg-gray-100 border-4 border-gray-900">
                <CardContent className="pt-6">
                  <div className="flex flex-col items-center">
                    <XCircle className="w-12 h-12 text-gray-900 mb-2 animate-pulse" />
                    <p className="text-4xl font-bold text-black">{metrics.musicIncorrect}</p>
                    <p className="text-sm text-black font-semibold">Music NOT Correct</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Music Issue Alert */}
            {metrics.musicIncorrect > 0 && (
              <Alert className="bg-gray-100 border-4 border-gray-900 mb-6">
                <AlertCircle className="h-8 w-8 text-gray-900" />
                <AlertDescription className="ml-4">
                  <p className="text-gray-900 text-xl font-extrabold mb-3 uppercase">
                    🚨 {metrics.musicIncorrect} GYMNAST(S) WITH INCORRECT MUSIC 🚨
                  </p>
                  <div className="space-y-2">
                    {metrics.musicIncorrectList.map((item, idx) => (
                      <div key={idx} className="bg-gray-100 p-3 rounded border-2 border-gray-500">
                        <p className="text-gray-900 font-bold text-lg">{item.name}</p>
                        <p className="text-gray-800 text-sm">
                          Club: {item.club} • Round: {item.round}
                        </p>
                      </div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Summary Info */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Overall Status - {getCompetitionName()}</CardTitle>
                <CardDescription>
                  Last updated: {lastRefreshTime?.toLocaleTimeString()} • Total Spectators: {statusData.summary.totalSpectators}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold">{statusData.summary.total}</p>
                    <p className="text-sm text-gray-600">Total Gymnasts</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{metrics.checkedIn}</p>
                    <p className="text-sm text-gray-600">Registered</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-orange-600">{metrics.notCheckedIn}</p>
                    <p className="text-sm text-gray-600">Pending</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{statusData.summary.totalSpectators}</p>
                    <p className="text-sm text-gray-600">Total Spectators</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Rounds Breakdown */}
            {statusData.rounds.map(round => (
              <Card key={round.roundNumber} className="mb-4">
                <CardHeader>
                  <CardTitle>{round.roundName}</CardTitle>
                  <CardDescription>
                    {round.summary.checkedIn}/{round.summary.total - round.summary.withdrawn} checked in 
                    {round.summary.withdrawn > 0 && ` • ${round.summary.withdrawn} withdrawn`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {round.gymnasts.map(gymnast => {
                      const status = gymnast.checkInStatus;
                      const isWithdrawn = status?.withdrawn;
                      const isCheckedIn = status?.checkedIn;
                      const musicIncorrect = status?.musicCorrect === false;

                      return (
                        <div
                          key={gymnast.id}
                          className={`p-3 rounded-lg border-2 ${
                            isWithdrawn
                              ? 'bg-gray-100 border-gray-400 opacity-60'
                              : musicIncorrect
                              ? 'bg-gray-100 border-gray-900 border-4'
                              : isCheckedIn
                              ? 'bg-green-50 border-green-400'
                              : 'bg-orange-50 border-orange-400'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold">
                                  {gymnast.preferredFirstName || gymnast.firstName} {gymnast.preferredLastName || gymnast.lastName}
                                </p>
                                {isWithdrawn && <Badge className="bg-gray-600">Withdrawn</Badge>}
                                {!isWithdrawn && isCheckedIn && (
                                  <Badge className="bg-green-600">Checked In</Badge>
                                )}
                                {!isWithdrawn && !isCheckedIn && (
                                  <Badge className="bg-orange-600">Not Checked In</Badge>
                                )}
                                {musicIncorrect && (
                                  <Badge className="bg-gray-900 animate-pulse">
                                    ⚠️ MUSIC ISSUE
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">
                                {gymnast.clubName} • BG: {gymnast.bgNumber} • {gymnast.category}
                              </p>
                              {status?.spectatorCount !== null && status?.spectatorCount !== undefined && (
                                <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                                  <Users className="w-4 h-4" />
                                  {status.spectatorCount} spectator(s)
                                </p>
                              )}
                              {status?.competingApparatus && status.competingApparatus.length > 0 && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Apparatus: {status.competingApparatus.map(a => a.charAt(0).toUpperCase() + a.slice(1)).join(', ')}
                                </p>
                              )}
                            </div>
                            {!isWithdrawn && isCheckedIn && (
                              <div className="flex items-center gap-2">
                                <Button
                                  data-testid={`edit-checkin-${gymnast.id}`}
                                  onClick={() => openEditDialog(gymnast, round.roundNumber)}
                                  variant="outline"
                                  size="sm"
                                  className="text-blue-700 hover:bg-blue-50 border-blue-300"
                                >
                                  <Pencil className="w-3 h-3 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  onClick={() => handleUndoCheckIn(
                                    gymnast.id,
                                    `${gymnast.preferredFirstName || gymnast.firstName} ${gymnast.preferredLastName || gymnast.lastName}`,
                                    selectedCompetition,
                                    round.roundNumber
                                  )}
                                  variant="outline"
                                  size="sm"
                                  className="text-gray-900 hover:bg-gray-100 border-gray-300"
                                >
                                  Undo
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </main>

      {/* Edit Check-In Dialog */}
      <Dialog open={!!editTarget} onOpenChange={(open) => { if (!open) setEditTarget(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Edit Check-In — {editTarget?.gymnast ? `${editTarget.gymnast.preferredFirstName || editTarget.gymnast.firstName} ${editTarget.gymnast.preferredLastName || editTarget.gymnast.lastName}` : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            {/* Competing Apparatus */}
            <div>
              <p className="text-sm font-semibold mb-2">Competing Apparatus</p>
              <div className="flex flex-wrap gap-2">
                {ALL_APPARATUS.map(app => (
                  <button
                    key={app.key}
                    data-testid={`edit-apparatus-${app.key}`}
                    onClick={() => toggleEditApparatus(app.key)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-colors ${
                      editApparatus.includes(app.key)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-blue-300'
                    }`}
                  >
                    {app.label}
                  </button>
                ))}
              </div>
              {editApparatus.length === 0 && (
                <p className="text-xs text-red-500 mt-1">Select at least one apparatus</p>
              )}
            </div>

            {/* Spectator Count */}
            <div>
              <p className="text-sm font-semibold mb-2">Spectator Count</p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setEditSpectators(Math.max(0, editSpectators - 1))}
                  className="w-8 h-8 rounded-full border-2 border-gray-300 text-gray-600 font-bold hover:bg-gray-100"
                >−</button>
                <span className="text-2xl font-bold w-10 text-center">{editSpectators}</span>
                <button
                  onClick={() => setEditSpectators(editSpectators + 1)}
                  className="w-8 h-8 rounded-full border-2 border-gray-300 text-gray-600 font-bold hover:bg-gray-100"
                >+</button>
              </div>
            </div>

            {/* Music Status */}
            <div>
              <p className="text-sm font-semibold mb-2">Floor Music</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditMusicCorrect(true)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-colors ${
                    editMusicCorrect === true ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-300'
                  }`}
                >Correct</button>
                <button
                  onClick={() => setEditMusicCorrect(false)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-colors ${
                    editMusicCorrect === false ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-600 border-gray-300'
                  }`}
                >Incorrect</button>
                <button
                  onClick={() => setEditMusicCorrect(null)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-colors ${
                    editMusicCorrect === null ? 'bg-gray-600 text-white border-gray-600' : 'bg-white text-gray-600 border-gray-300'
                  }`}
                >N/A</button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button
              data-testid="save-edit-checkin"
              onClick={handleSaveEdit}
              disabled={saving || editApparatus.length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
